import * as THREE from "three";
import { Reflector } from "three/examples/jsm/objects/Reflector.js";
import { Brush, Evaluator, SUBTRACTION } from "three-bvh-csg";
import { ContactShadow, FLOOR_SHADOW_LOOK } from "./ContactShadow.js";
import { DoorManager } from "./DoorManager.js";
import { WALL_THICKNESS } from "../utils/constants.js";
import { forEachWorldCollisionBox } from "../utils/geometry.js";
import { createSkyMask } from "../sky/skyMask.js";
import {
  applyFloorEdgeFade,
  applyReflectorEdgeFade,
  createFloorFadeAlphaMap,
  createFloorFadeUniforms,
  environmentWantsFloorFade,
} from "./scene/floorFade.js";

// Floor images run their width along the room's depth, as they did when the room was viewed from -X.
const FLOOR_QUARTER_TURN = true;

const MATERIAL_TEXTURE_SLOTS = [
  "map", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "displacementMap",
  "emissiveMap", "alphaMap", "bumpMap", "lightMap",
];

/**
 * RoomBuilder - Handles creation and management of room geometry
 *
 * Responsibilities:
 * - Creating floor, walls, ceiling, windows, door, trim
 * - Managing room size and geometry updates
 * - Material application to room elements
 * - Visibility control for room components
 */
export class RoomBuilder {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;

    // Room elements
    this.floor = null;
    this.floorReflector = null;
    this.walls = [];
    this.ceiling = null;
    this.windows = [];
    this.trims = [];
    this.doorManager = null;

    // Room dimensions
    this.roomSize = { width: 10, depth: 10, height: 3.5 };

    // Lighting elements managed by room builder
    this.ceilingLight = null;
    this.contactShadow = null;

    // Track scene mode for ceiling side (2D mode hides ceiling from above)
    this.is2DMode = false;

    // White floor override
    this.whiteFloorEnabled = false;
    this._savedFloorMaterial = null;
    // The override material itself, so a rebuilt floor can be recognised as un-overridden.
    this._whiteFloorMaterial = null;

    // Shared radial-fade uniforms for the floor, contact shadow and reflector.
    this._floorFade = createFloorFadeUniforms();
    // The floor material's own alpha state while the path tracer borrows it.
    this._traceFloorFade = null;
  }

  /**
   * Create the entire room based on current environment settings
   */
  createRoom() {
    this.removeExistingRoom();

    const envManager = this.sceneManager.getEnvironmentManager();
    const currentEnv = envManager.environments?.[envManager.currentEnvironment];

    const showWalls = currentEnv?.visibility?.walls !== false;
    const showCeiling = currentEnv?.visibility?.ceiling !== false;
    const showFloor = currentEnv?.visibility?.floor !== false;

    if (showFloor) {
      this.createFloor();
    }

    if (showWalls) {
      this.createWalls();
      this.createWindows();
      this.createDoor();
    }

    if (showCeiling) {
      this.createCeiling();
    }

    this.createCeilingLight();

    // The override lives on the floor mesh this just replaced — re-dress it.
    if (this.whiteFloorEnabled && this.floor) this._applyWhiteFloorOverride();
  }

  /**
   * Remove all existing room elements
   */
  removeExistingRoom() {
    const scene = this.sceneManager.getScene();
    if (!scene) return;

    if (this.floor) {
      this._removeAndDispose(scene, this.floor);
      this.floor = null;
    }

    // The mirror is a floor-sized plane, so it can't outlive the floor it overlays.
    this._removeFloorReflector();

    this._removeObjectsByUserData(scene, 'isGrid');

    // Windows are Groups (frame, glass, muntins), so each subtree is walked.
    for (const list of [this.walls, this.windows, this.trims]) {
      list.forEach((obj) => obj && this._removeAndDispose(scene, obj));
    }
    this.walls = [];
    this.windows = [];
    this.trims = [];

    if (this.ceiling) {
      this._removeAndDispose(scene, this.ceiling);
      this.ceiling = null;
    }

    // Remove ceiling light
    if (this.ceilingLight) {
      scene.remove(this.ceilingLight);
      if (this.ceilingLight.target) {
        scene.remove(this.ceilingLight.target);
      }
      this.ceilingLight = null;
    }

    // Remove door
    if (this.doorManager) {
      this.doorManager.dispose();
      this.doorManager = null;
    }

    // Remove contact shadow
    if (this.contactShadow) {
      scene.remove(this.contactShadow.getMesh());
      this.contactShadow.dispose();
      this.contactShadow = null;
    }
  }

  /**
   * Helper to remove objects by userData property
   */
  _removeObjectsByUserData(scene, property) {
    const objectsToRemove = [];
    scene.traverse((object) => {
      if (object.userData[property]) {
        objectsToRemove.push(object);
      }
    });
    objectsToRemove.forEach(obj => {
      scene.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) obj.material.dispose();
    });
  }

  // ===== FLOOR CREATION =====

  createFloor() {
    const envManager = this.sceneManager.getEnvironmentManager();
    const materialData = envManager.floorMaterials[envManager.currentFloorMaterial];
    this._createTexturedFloor(materialData);
  }

  // Image-width / image-height extent for square texels; quarter-turned, so depth over width.
  _floorAspect() {
    return this.roomSize.depth / this.roomSize.width;
  }

  _createTexturedFloor(materialData) {
    const scene = this.sceneManager.getScene();
    const geometry = new THREE.PlaneGeometry(this.roomSize.width, this.roomSize.depth);
    const material = this.sceneManager.createMaterial(materialData, false, true, true, this._floorAspect(), FLOOR_QUARTER_TURN);

    this.floor = new THREE.Mesh(geometry, material);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = true;
    this.floor.castShadow = true;
    this.floor.name = "ground";
    this.floor.userData.isFloor = true;

    scene.add(this.floor);
    this._dressFloorMaterial(material);

    // The rectangle outline reads as a hard studio cutoff; skip it when the floor melts into the backdrop.
    if (!this._wantsFloorFade()) {
      const edges = new THREE.EdgesGeometry(geometry);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x666666,
        transparent: true,
        opacity: 0.1,
      });
      const wireframe = new THREE.LineSegments(edges, lineMaterial);
      wireframe.rotation.x = -Math.PI / 2;
      wireframe.position.y = 0.001;
      wireframe.userData.isGrid = true;
      scene.add(wireframe);
    }
  }

  /**
   * Toggle an optional grid overlay on the floor
   */
  toggleGridOverlay(show) {
    const scene = this.sceneManager.getScene();
    if (!scene) return;

    // Remove existing grid overlay
    const toRemove = [];
    scene.traverse((obj) => {
      if (obj.userData.isGridOverlay) toRemove.push(obj);
    });
    toRemove.forEach((obj) => {
      scene.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) obj.material.dispose();
    });

    if (show) {
      const gridSize = Math.max(this.roomSize.width, this.roomSize.depth);
      const divisions = Math.round(gridSize);
      const gridHelper = new THREE.GridHelper(gridSize, divisions, 0x000000, 0x000000);
      gridHelper.position.y = 0.002;
      gridHelper.userData.isGridOverlay = true;
      scene.add(gridHelper);
    }
  }

  /**
   * Apply or remove a solid white floor override (persists until toggled off)
   */
  setWhiteFloor(enable) {
    if (!this.floor) return;
    if (enable) {
      this._applyWhiteFloorOverride();
      this.whiteFloorEnabled = true;
    } else if (this.whiteFloorEnabled) {
      this._removeFloorReflector();
      this._disposeMaterial(this.floor.material);
      this.floor.material = this._savedFloorMaterial;
      this._savedFloorMaterial = null;
      this._whiteFloorMaterial = null;
      this.whiteFloorEnabled = false;
    }
  }

  // Keyed on the material instance, not whiteFloorEnabled, so it re-dresses a rebuilt floor.
  _applyWhiteFloorOverride() {
    if (this.floor.material === this._whiteFloorMaterial) return;
    // A rebuild orphans the material held for the un-toggle.
    this._disposeMaterial(this._savedFloorMaterial);
    this._savedFloorMaterial = this.floor.material;
    // Opaque white base; _addFloorReflector overlays the reflection.
    this._whiteFloorMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 });
    this.floor.material = this._whiteFloorMaterial;
    this._dressFloorMaterial(this._whiteFloorMaterial);
    this._addFloorReflector();
  }

  // Reflection alpha fades from full strength (FADE_START) to nothing (FADE_END),
  // measured in metres from the nearest furniture footprint box.
  static REFLECT_STRENGTH = 0.3;
  static REFLECT_FADE_START = 0.0;
  static REFLECT_FADE_END = 0.2;
  static REFLECT_MAX_BOXES = 16; // footprint boxes fed to the shader

  /**
   * Add a planar mirror (THREE.Reflector) as a transparent floor overlay whose alpha
   * fades by distance to the furniture footprint (fed in as uniforms each frame), so
   * the reflection hugs the sofa shape and melts into the floor.
   */
  _addFloorReflector() {
    if (this.floorReflector) return;
    if (this.sceneManager.environmentManager?.environmentSettings?.reflector === false) return;
    const scene = this.sceneManager.getScene();
    const res = this.sceneManager.quality.reduced ? 512 : 1024;
    const MAX = RoomBuilder.REFLECT_MAX_BOXES;
    const geometry = new THREE.PlaneGeometry(this.roomSize.width, this.roomSize.depth);
    const reflector = new Reflector(geometry, {
      textureWidth: res,
      textureHeight: res,
      color: 0x555555, // tints/dims the reflected image
    });
    reflector.rotation.x = -Math.PI / 2;
    reflector.position.y = 0.004; // just above the floor; transparent overlay
    reflector.userData.isFloor = true;

    // Transparent overlay + footprint-distance alpha fade.
    const mat = reflector.material;
    mat.transparent = true;
    mat.depthWrite = false;
    mat.uniforms.reflectStrength = { value: RoomBuilder.REFLECT_STRENGTH };
    mat.uniforms.fadeStart = { value: RoomBuilder.REFLECT_FADE_START };
    mat.uniforms.fadeEnd = { value: RoomBuilder.REFLECT_FADE_END };
    mat.uniforms.boxCount = { value: 0 };
    mat.uniforms.boxMin = { value: Array.from({ length: MAX }, () => new THREE.Vector2()) };
    mat.uniforms.boxMax = { value: Array.from({ length: MAX }, () => new THREE.Vector2()) };
    mat.vertexShader = mat.vertexShader
      .replace('varying vec4 vUv;', 'varying vec4 vUv;\n\t\tvarying vec3 vWorldPosition;')
      .replace(
        'vUv = textureMatrix * vec4( position, 1.0 );',
        'vUv = textureMatrix * vec4( position, 1.0 );\n\t\t\tvWorldPosition = ( modelMatrix * vec4( position, 1.0 ) ).xyz;'
      );
    mat.fragmentShader = mat.fragmentShader
      .replace(
        'varying vec4 vUv;',
        `varying vec4 vUv;\n\t\tvarying vec3 vWorldPosition;\n\t\tuniform float reflectStrength;\n\t\tuniform float fadeStart;\n\t\tuniform float fadeEnd;\n\t\tuniform int boxCount;\n\t\tuniform vec2 boxMin[${MAX}];\n\t\tuniform vec2 boxMax[${MAX}];`
      )
      .replace(
        'gl_FragColor = vec4( blendOverlay( base.rgb, color ), 1.0 );',
        `vec2 fp = vWorldPosition.xz;\n\t\t\tfloat fpDist = 1.0e6;\n\t\t\tfor ( int i = 0; i < ${MAX}; i++ ) {\n\t\t\t\tif ( i >= boxCount ) break;\n\t\t\t\tvec2 d = max( boxMin[i] - fp, fp - boxMax[i] );\n\t\t\t\tfpDist = min( fpDist, length( max( d, vec2( 0.0 ) ) ) );\n\t\t\t}\n\t\t\tfloat reflFade = reflectStrength * ( 1.0 - smoothstep( fadeStart, fadeEnd, fpDist ) );\n\t\t\tgl_FragColor = vec4( blendOverlay( base.rgb, color ), reflFade );`
      );
    this._syncFloorFadeUniforms();
    applyReflectorEdgeFade(mat, this._floorFade);
    mat.needsUpdate = true;

    // Refresh footprint boxes each frame, just before the mirror draws.
    const baseOnBeforeRender = reflector.onBeforeRender;
    reflector.onBeforeRender = (r, s, c, geo, m, group) => {
      this._updateReflectorFootprint();
      baseOnBeforeRender.call(reflector, r, s, c, geo, m, group);
    };

    this.floorReflector = reflector;
    scene.add(reflector);
  }

  /** Feed each module's world-space footprint box into the reflector's shader. */
  _updateReflectorFootprint() {
    const mat = this.floorReflector?.material;
    if (!mat) return;
    const all = this.sceneManager.app?.getFurnitureManager?.()?.getAllFurniture?.() || [];
    const boxMin = mat.uniforms.boxMin.value;
    const boxMax = mat.uniforms.boxMax.value;
    const MAX = boxMin.length;

    let count = 0;
    const addBox = (cx, cy, cz, hw, hh, hd) => {
      if (count >= MAX) return;
      boxMin[count].set(cx - hw, cz - hd);
      boxMax[count].set(cx + hw, cz + hd);
      count++;
    };
    for (const f of all) {
      if (count >= MAX) break;
      if (f.furnitureData?.componentType !== "module") continue;
      forEachWorldCollisionBox(f, 0, addBox);
    }
    mat.uniforms.boxCount.value = count;
  }

  _removeFloorReflector() {
    if (!this.floorReflector) return;
    const scene = this.sceneManager.getScene();
    scene.remove(this.floorReflector);
    this.floorReflector.geometry?.dispose();
    this.floorReflector.material?.dispose();
    this.floorReflector.getRenderTarget?.()?.dispose();
    this.floorReflector = null;
  }

  /**
   * Update floor material without recreating geometry
   */
  updateFloorMaterial(materialKey) {
    if (!this.floor || !this.floor.material) return;

    const envManager = this.sceneManager.getEnvironmentManager();
    const materialData = envManager.floorMaterials[materialKey];
    const newMaterial = this.sceneManager.createMaterial(materialData, false, true, true, this._floorAspect(), FLOOR_QUARTER_TURN);

    if (this.whiteFloorEnabled) {
      this._disposeMaterial(this._savedFloorMaterial);
      this._savedFloorMaterial = newMaterial;
      this._dressFloorMaterial(newMaterial);
    } else {
      this._disposeMaterial(this.floor.material);
      this.floor.material = newMaterial;
      this._dressFloorMaterial(newMaterial);
    }
  }

  applyCustomFloorTexture(dataURL) {
    if (!this.floor) return;
    const sm = this.sceneManager;
    const texture = sm.textureLoader.load(dataURL);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = sm.renderer.capabilities.getMaxAnisotropy();
    texture.repeat.set(1, 1);

    const newMaterial = new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.4,
      metalness: 0.0,
      side: THREE.DoubleSide,
      shadowSide: THREE.DoubleSide,
    });
    // Before dressing: it is what turns the edge fade off.
    this.customFloorTextureDataURL = dataURL;

    if (this.whiteFloorEnabled) {
      this._disposeMaterial(this._savedFloorMaterial);
      this._savedFloorMaterial = newMaterial;
      this._dressFloorMaterial(newMaterial);
    } else {
      this._disposeMaterial(this.floor.material);
      this.floor.material = newMaterial;
      this._dressFloorMaterial(newMaterial);
    }
  }

  clearCustomFloorTexture() {
    if (!this.customFloorTextureDataURL) return;
    this.customFloorTextureDataURL = null;
    const envManager = this.sceneManager.getEnvironmentManager();
    this.updateFloorMaterial(envManager.currentFloorMaterial);
  }

  // ===== WALL CREATION =====

  createWalls() {
    const envManager = this.sceneManager.getEnvironmentManager();
    const wallHeight = this.roomSize.height;
    const wallThickness = WALL_THICKNESS;
    const materialData = envManager.wallMaterials[envManager.currentWallMaterial];

    const wallConfigs = this._getWallConfigs(wallHeight);
    // Solid walls share one material; a window wall builds its own (its CSG UVs need unrepeated textures).
    let sharedMaterial = null;

    wallConfigs.forEach((config, index) => {
      const windowConfig = this._wallWindow(config.name);

      if (!windowConfig) {
        sharedMaterial ??= this.sceneManager.createMaterial(materialData, false);
        this._createSolidWall(config, index, wallThickness, sharedMaterial);
      } else {
        const windowStyle = envManager.windowConfiguration.windowStyles[windowConfig.style];
        this._createWallWithWindow(config, index, wallThickness, windowStyle.dimensions);
      }

      this._addBaseboardToWall(config, wallThickness);
      this._addCrownMoldingToWall(config, wallThickness);
    });
  }

  // First window declared on a wall in the environment's window configuration, or null.
  _wallWindow(wallName) {
    const wallConfig = this.sceneManager.getEnvironmentManager().windowConfiguration?.wallConfigurations?.[wallName];
    return wallConfig?.hasWindows && wallConfig.windows.length > 0 ? wallConfig.windows[0] : null;
  }

  _getWallConfigs(wallHeight) {
    return [
      {
        name: 'north',
        width: this.roomSize.width,
        height: wallHeight,
        position: [0, wallHeight / 2, -this.roomSize.depth / 2],
        rotation: [0, 0, 0],
      },
      {
        name: 'south',
        width: this.roomSize.width,
        height: wallHeight,
        position: [0, wallHeight / 2, this.roomSize.depth / 2],
        rotation: [0, 0, 0],
      },
      {
        name: 'east',
        width: this.roomSize.depth,
        height: wallHeight,
        position: [this.roomSize.width / 2, wallHeight / 2, 0],
        rotation: [0, Math.PI / 2, 0],
      },
      {
        name: 'west',
        width: this.roomSize.depth,
        height: wallHeight,
        position: [-this.roomSize.width / 2, wallHeight / 2, 0],
        rotation: [0, Math.PI / 2, 0],
      },
    ];
  }

  _createSolidWall(config, index, wallThickness, material) {
    const scene = this.sceneManager.getScene();
    const widthSegments = Math.ceil(config.width * 2);
    const heightSegments = Math.ceil(config.height * 2);

    const geometry = new THREE.BoxGeometry(
      config.width,
      config.height,
      wallThickness,
      widthSegments,
      heightSegments,
      1,
    );

    const wall = new THREE.Mesh(geometry, material);
    wall.position.set(...config.position);
    wall.rotation.set(...config.rotation);
    wall.receiveShadow = true;
    wall.castShadow = true;
    wall.name = `wall_${index}`;
    wall.userData.isWall = true;
    wall.userData.wallName = config.name;
    wall.renderOrder = 0;

    this.walls.push(wall);
    scene.add(wall);
  }

  _createWallWithWindow(config, index, wallThickness, windowDimensions) {
    const scene = this.sceneManager.getScene();
    const envManager = this.sceneManager.getEnvironmentManager();

    const { bottomHeight, topHeight, widthRatio, gapBetweenWindows, windowsPerWall } = windowDimensions;
    const totalWindowArea = config.width * widthRatio;
    const totalGaps = windowsPerWall > 1 ? gapBetweenWindows * (windowsPerWall - 1) : 0;
    const singleWindowWidth = (totalWindowArea - totalGaps) / windowsPerWall;
    const windowHeight = topHeight - bottomHeight;
    const windowCenterY = (bottomHeight + topHeight) / 2;

    // Create wall with CSG
    const widthSegments = Math.ceil(config.width * 2);
    const heightSegments = Math.ceil(config.height * 2);
    const wallGeometry = new THREE.BoxGeometry(
      config.width, config.height, wallThickness,
      widthSegments, heightSegments, 1
    );
    const wallBrush = new Brush(wallGeometry);
    const evaluator = new Evaluator();

    let resultBrush = wallBrush;

    for (let i = 0; i < windowsPerWall; i++) {
      let xOffset = this._calculateWindowXOffset(i, windowsPerWall, singleWindowWidth, gapBetweenWindows);

      const windowGeometry = new THREE.BoxGeometry(
        singleWindowWidth, windowHeight, wallThickness + 0.1
      );
      const windowBrush = new Brush(windowGeometry);
      windowBrush.position.set(xOffset, windowCenterY - config.height / 2, 0);
      windowBrush.updateMatrixWorld();

      resultBrush = evaluator.evaluate(resultBrush, windowBrush, SUBTRACTION);
    }

    const materialData = envManager.wallMaterials[envManager.currentWallMaterial];
    const csgMaterial = this.sceneManager.createMaterial(materialData, false, true, false);
    const wall = new THREE.Mesh(resultBrush.geometry, csgMaterial);

    this._fixCSGUVMapping(wall.geometry, materialData, config.width, config.height);

    wall.receiveShadow = true;
    wall.castShadow = true;
    wall.userData.isWall = true;
    wall.userData.wallName = config.name;
    wall.userData.hasCSGGeometry = true;
    wall.userData.wallWidth = config.width;
    wall.userData.wallHeight = config.height;
    wall.name = `wall_${index}`;
    wall.renderOrder = 0;

    // Position all walls correctly based on config
    wall.position.set(...config.position);
    wall.rotation.set(...config.rotation);

    this.walls.push(wall);
    scene.add(wall);
  }

  _calculateWindowXOffset(index, windowsPerWall, singleWindowWidth, gapBetweenWindows) {
    if (windowsPerWall === 1) return 0;

    if (windowsPerWall === 2) {
      return index === 0
        ? -singleWindowWidth / 2 - gapBetweenWindows / 2
        : singleWindowWidth / 2 + gapBetweenWindows / 2;
    }

    const totalSpacing = (windowsPerWall - 1) * gapBetweenWindows;
    const totalWidth = windowsPerWall * singleWindowWidth + totalSpacing;
    return -totalWidth / 2 + singleWindowWidth / 2 + index * (singleWindowWidth + gapBetweenWindows);
  }

  /**
   * Update wall material for all walls
   */
  updateWallMaterial(materialKey) {
    const envManager = this.sceneManager.getEnvironmentManager();
    const materialData = envManager.wallMaterials[materialKey];

    this.walls.forEach((wall) => {
      const hasCSGGeometry = wall.userData.hasCSGGeometry === true;
      const newMaterial = this.sceneManager.createMaterial(materialData, false, true, !hasCSGGeometry);

      this._disposeMaterial(wall.material);
      wall.material = newMaterial;

      if (hasCSGGeometry && wall.userData.wallWidth && wall.userData.wallHeight) {
        this._fixCSGUVMapping(wall.geometry, materialData, wall.userData.wallWidth, wall.userData.wallHeight);
      }
    });
  }

  // ===== WINDOW CREATION =====

  createWindows() {
    const envManager = this.sceneManager.getEnvironmentManager();

    this._getWallConfigs(this.roomSize.height).forEach((wallConfig, index) => {
      const windowConfig = this._wallWindow(wallConfig.name);

      if (windowConfig) {
        const windowStyle = envManager.windowConfiguration.windowStyles[windowConfig.style];
        const frameMaterialData = envManager.windowConfiguration.frameMaterials[windowConfig.frameMaterial];
        const glassMaterialData = envManager.windowConfiguration.glassMaterials[windowConfig.glassMaterial];

        this._createWindowOnWall(index, wallConfig, windowStyle, frameMaterialData, glassMaterialData);
      }
    });
  }

  _createWindowOnWall(wallIndex, wallConfig, windowStyle, frameMaterialData, glassMaterialData) {
    const scene = this.sceneManager.getScene();
    const { position: wallPosition, rotation: wallRotation, width: wallWidth, name: wallName } = wallConfig;
    const dimensions = windowStyle.dimensions;
    const features = windowStyle.features;

    const totalWindowWidth = wallWidth * dimensions.widthRatio;
    const windowHeight = dimensions.topHeight - dimensions.bottomHeight;
    const windowCenterY = (dimensions.bottomHeight + dimensions.topHeight) / 2;

    const frameProps = frameMaterialData.properties;
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: parseInt(frameProps.color.replace('#', '0x')),
      roughness: frameProps.roughness,
      metalness: frameProps.metalness,
      envMapIntensity: frameProps.envMapIntensity,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const frameThickness = dimensions.frameThickness;
    const frameDepth = dimensions.frameDepth;
    const muntinThickness = dimensions.muntinThickness;
    const gapBetweenWindows = dimensions.gapBetweenWindows;
    const windowsPerWall = dimensions.windowsPerWall;
    const frameWidth = dimensions.frameWidth;

    const totalGaps = windowsPerWall > 1 ? gapBetweenWindows * (windowsPerWall - 1) : 0;
    const singleWindowWidth = (totalWindowWidth - totalGaps) / windowsPerWall;

    const glassProps = glassMaterialData.properties;
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: parseInt(glassProps.color.replace('#', '0x')),
      metalness: glassProps.metalness,
      roughness: glassProps.roughness,
      transparent: true,
      opacity: glassProps.opacity,
      side: THREE.DoubleSide,
      envMapIntensity: glassProps.envMapIntensity,
    });

    const windowGroup = new THREE.Group();
    windowGroup.userData.isWindow = true;
    windowGroup.userData.wallName = wallName;

    for (let i = 0; i < windowsPerWall; i++) {
      let xOffset = this._calculateWindowXOffset(i, windowsPerWall, singleWindowWidth, gapBetweenWindows);

      // Create frame
      const frame = this._createSimpleWindowFrame(singleWindowWidth, windowHeight, frameWidth, frameDepth, frameMaterial);
      frame.position.set(xOffset, 0, 0);
      windowGroup.add(frame);

      // Create glass pane
      const glassPane = new THREE.Mesh(
        new THREE.PlaneGeometry(singleWindowWidth, windowHeight),
        glassMaterial
      );
      glassPane.position.set(xOffset, 0, -0.01);
      glassPane.castShadow = false;
      glassPane.receiveShadow = true;
      glassPane.renderOrder = 1;
      windowGroup.add(glassPane);
      windowGroup.add(createSkyMask(glassPane));

      // Create muntins
      this._createMuntins(windowGroup, features, dimensions, xOffset, singleWindowWidth, windowHeight, frameMaterial, muntinThickness);
    }

    // Position window group
    this._positionWindowGroup(windowGroup, wallIndex, wallPosition, wallRotation, windowCenterY);

    this.windows.push(windowGroup);
    scene.add(windowGroup);
  }

  _createSimpleWindowFrame(width, height, frameWidth, depth, material) {
    const frame = new THREE.Group();

    const createBar = (w, h, d, pos) => {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
      bar.position.set(...pos);
      bar.castShadow = true;
      bar.receiveShadow = true;
      bar.renderOrder = 2;
      return bar;
    };

    frame.add(createBar(width, frameWidth, depth, [0, height / 2 - frameWidth / 2, 0]));
    frame.add(createBar(width, frameWidth, depth, [0, -height / 2 + frameWidth / 2, 0]));
    frame.add(createBar(frameWidth, height - frameWidth * 2, depth, [-width / 2 + frameWidth / 2, 0, 0]));
    frame.add(createBar(frameWidth, height - frameWidth * 2, depth, [width / 2 - frameWidth / 2, 0, 0]));

    return frame;
  }

  _createMuntins(windowGroup, features, dimensions, xOffset, singleWindowWidth, windowHeight, frameMaterial, muntinThickness) {
    if (!features.hasMuntins) return;

    const muntinDepth = muntinThickness * 1.5;

    if (features.muntinPattern === 'cross') {
      // Vertical muntin
      const verticalMuntin = new THREE.Mesh(
        new THREE.BoxGeometry(muntinThickness, windowHeight, muntinDepth),
        frameMaterial
      );
      verticalMuntin.position.set(xOffset, 0, 0);
      verticalMuntin.castShadow = true;
      verticalMuntin.receiveShadow = true;
      windowGroup.add(verticalMuntin);

      // Horizontal muntin
      const horizontalMuntin = new THREE.Mesh(
        new THREE.BoxGeometry(singleWindowWidth, muntinThickness, muntinDepth),
        frameMaterial
      );
      horizontalMuntin.position.set(xOffset, 0, 0);
      horizontalMuntin.castShadow = true;
      horizontalMuntin.receiveShadow = true;
      windowGroup.add(horizontalMuntin);
    } else if (features.muntinPattern === 'grid') {
      this._createGridMuntins(windowGroup, dimensions, xOffset, singleWindowWidth, windowHeight, frameMaterial, muntinThickness, muntinDepth);
    }
  }

  _createGridMuntins(windowGroup, dimensions, xOffset, singleWindowWidth, windowHeight, frameMaterial, muntinThickness, muntinDepth) {
    const gridColumns = dimensions.gridColumns || 3;
    const gridRows = dimensions.gridRows || 4;
    const middleThicknessMultiplier = 2.5;
    const middleDepthMultiplier = 2.0;

    // Vertical muntins
    for (let col = 1; col < gridColumns; col++) {
      const xPosition = xOffset - singleWindowWidth / 2 + (singleWindowWidth / gridColumns) * col;
      const isMiddleVertical = (gridColumns % 2 === 0) && (col === gridColumns / 2);
      const currentThickness = isMiddleVertical ? muntinThickness * middleThicknessMultiplier : muntinThickness;
      const currentDepth = isMiddleVertical ? muntinDepth * middleDepthMultiplier : muntinDepth;

      const verticalMuntin = new THREE.Mesh(
        new THREE.BoxGeometry(currentThickness, windowHeight, currentDepth),
        frameMaterial
      );
      verticalMuntin.position.set(xPosition, 0, 0);
      verticalMuntin.castShadow = true;
      verticalMuntin.receiveShadow = true;
      windowGroup.add(verticalMuntin);
    }

    // Horizontal muntins
    for (let row = 1; row < gridRows; row++) {
      const yPosition = -windowHeight / 2 + (windowHeight / gridRows) * row;
      const isMiddleHorizontal = (gridRows % 2 === 0) && (row === gridRows / 2);
      const currentThickness = isMiddleHorizontal ? muntinThickness * middleThicknessMultiplier : muntinThickness;
      const currentDepth = isMiddleHorizontal ? muntinDepth * middleDepthMultiplier : muntinDepth;

      const horizontalMuntin = new THREE.Mesh(
        new THREE.BoxGeometry(singleWindowWidth, currentThickness, currentDepth),
        frameMaterial
      );
      horizontalMuntin.position.set(xOffset, yPosition, 0);
      horizontalMuntin.castShadow = true;
      horizontalMuntin.receiveShadow = true;
      windowGroup.add(horizontalMuntin);
    }
  }

  _positionWindowGroup(windowGroup, wallIndex, wallPosition, wallRotation, windowCenterY) {
    if (wallIndex === 0 || wallIndex === 1) {
      windowGroup.position.set(0, windowCenterY, wallPosition[2]);
      windowGroup.rotation.set(...wallRotation);
    } else {
      windowGroup.position.set(wallPosition[0], windowCenterY, 0);
      windowGroup.rotation.set(...wallRotation);
    }
  }

  // ===== CEILING =====

  createCeiling() {
    const scene = this.sceneManager.getScene();
    const envManager = this.sceneManager.getEnvironmentManager();

    const widthSegments = Math.ceil(this.roomSize.width * 2);
    const heightSegments = Math.ceil(this.roomSize.depth * 2);
    const geometry = new THREE.PlaneGeometry(
      this.roomSize.width,
      this.roomSize.depth,
      widthSegments,
      heightSegments,
    );

    const materialData = envManager.wallMaterials['white_wall'];
    const material = this.sceneManager.createMaterial(materialData, true);

    // Apply correct side based on current scene mode
    material.side = this.is2DMode ? 0 : 2; // THREE.FrontSide = 0, THREE.DoubleSide = 2

    this.ceiling = new THREE.Mesh(geometry, material);
    this.ceiling.rotation.x = Math.PI / 2;
    this.ceiling.position.y = this.roomSize.height;
    this.ceiling.receiveShadow = true;
    this.ceiling.castShadow = true;
    this.ceiling.name = "ceiling";
    this.ceiling.userData.isCeiling = true;

    scene.add(this.ceiling);
  }

  // ===== DOOR =====

  createDoor() {
    const scene = this.sceneManager.getScene();
    this.doorManager = new DoorManager(scene, null, this.roomSize);
    this.doorManager.addToScene(scene);
  }

  getDoorManager() {
    return this.doorManager;
  }

  // ===== CEILING LIGHT =====

  createCeilingLight() {
    const scene = this.sceneManager.getScene();
    const renderer = this.sceneManager.getRenderer();

    const lightColor = 0xfff4e0;
    const intensity = 4.0;
    const distance = 30;
    const angle = Math.PI / 2.2;
    const penumbra = 0.8;
    const decay = 0.5;

    this.ceilingLight = new THREE.SpotLight(lightColor, intensity, distance, angle, penumbra, decay);
    this.ceilingLight.name = "ceilingFixture";
    this.ceilingLight.position.set(0, this.roomSize.height - 0.15, 0);
    this.ceilingLight.target.position.set(0, 0, 0);
    this.ceilingLight.castShadow = false;

    // Create contact shadow system
    this.contactShadow = new ContactShadow(renderer, scene, {
      planeWidth: this.roomSize.width,
      planeHeight: this.roomSize.depth,
      ...FLOOR_SHADOW_LOOK,
      renderTargetSize: this.sceneManager.quality.reduced ? 512 : 1024
    });

    scene.add(this.ceilingLight);
    scene.add(this.ceilingLight.target);
    scene.add(this.contactShadow.getMesh());
    this._dressFloorMaterial(this.contactShadow.getMesh().material);
  }

  getCeilingLight() {
    return this.ceilingLight;
  }

  getContactShadow() {
    return this.contactShadow;
  }

  // ===== TRIM (BASEBOARD & CROWN MOLDING) =====

  _addBaseboardToWall(wallConfig, wallThickness) {
    const trim = this.sceneManager.getEnvironmentManager().wallTrimConfiguration;
    if (!trim?.settings?.enableBaseboard) return;
    const { height, depth } = trim.baseboard;
    this._addTrimToWall(wallConfig, wallThickness, trim, {
      height, depth, centerY: height / 2, name: `baseboard_${wallConfig.name}`, flag: 'isBaseboard',
    });
  }

  _addCrownMoldingToWall(wallConfig, wallThickness) {
    const trim = this.sceneManager.getEnvironmentManager().wallTrimConfiguration;
    if (!trim?.settings?.enableCrownMolding) return;
    const { height, depth, offsetFromCeiling } = trim.crownMolding;
    this._addTrimToWall(wallConfig, wallThickness, trim, {
      height, depth, centerY: this.roomSize.height - height / 2 - offsetFromCeiling,
      name: `crown_${wallConfig.name}`, flag: 'isCrownMolding',
    });
  }

  // A full-width strip on the room side of a wall, sunk 5 mm into it so no seam shows.
  _addTrimToWall(wallConfig, wallThickness, trim, { height, depth, centerY, name, flag }) {
    const materialData = trim.materials[trim.settings.defaultMaterial];
    const geometry = new THREE.BoxGeometry(wallConfig.width, height, depth);
    const material = this.sceneManager.createMaterial(materialData, false, false);
    const mesh = new THREE.Mesh(geometry, material);

    const [wallX, , wallZ] = wallConfig.position;
    const [rotX, rotY, rotZ] = wallConfig.rotation;
    const inset = wallThickness / 2 + depth / 2 - 0.005;
    // North/south walls face along Z, east/west along X; step toward the room centre.
    const xOffset = rotY === 0 ? 0 : (wallX > 0 ? -inset : inset);
    const zOffset = rotY === 0 ? (wallZ > 0 ? -inset : inset) : 0;

    mesh.rotation.set(rotX, rotY, rotZ);
    mesh.position.set(wallX + xOffset, centerY, wallZ + zOffset);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = name;
    mesh.userData[flag] = true;
    mesh.userData.wallName = wallConfig.name;

    this.trims.push(mesh);
    this.sceneManager.getScene().add(mesh);
  }

  // ===== UV MAPPING =====

  _fixCSGUVMapping(geometry, materialData, wallWidth, wallHeight) {
    if (!geometry.attributes.normal) {
      geometry.computeVertexNormals();
    }

    const positions = geometry.attributes.position;
    const normals = geometry.attributes.normal;
    const uvs = [];

    const repeat = materialData?.properties?.repeat || [4, 4];
    // Walls bake the repeat into their UVs, so a derived (scalar) repeat tiles squarely here.
    const [uRepeat, vRepeat] = typeof repeat === 'number' ? [repeat, repeat] : repeat;
    const uScale = uRepeat / wallWidth;
    const vScale = vRepeat / wallHeight;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);

      const nx = Math.abs(normals.getX(i));
      const ny = Math.abs(normals.getY(i));
      const nz = Math.abs(normals.getZ(i));

      let u, v;

      if (nx > ny && nx > nz) {
        u = z * uScale;
        v = y * vScale;
      } else if (nz > nx && nz > ny) {
        u = x * uScale;
        v = y * vScale;
      } else {
        u = x * uScale;
        v = z * uScale;
      }

      uvs.push(u, v);
    }

    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.attributes.uv.needsUpdate = true;
  }

  // ===== VISIBILITY CONTROLS =====

  setFloorVisible(visible) {
    if (this.floor) this.floor.visible = visible;
  }

  _wantsFloorFade() {
    // An uploaded floor plan is the room itself: fading its edges hides the walls you are measuring against.
    if (this.customFloorTextureDataURL) return false;
    const envManager = this.sceneManager.getEnvironmentManager();
    const env = envManager?.environments?.[envManager.currentEnvironment];
    return environmentWantsFloorFade(env);
  }

  _syncFloorFadeUniforms() {
    const envManager = this.sceneManager.getEnvironmentManager();
    const env = envManager?.environments?.[envManager.currentEnvironment];
    this._floorFade.uFloorFade.value = this._wantsFloorFade() ? 1 : 0;
    this._floorFade.uFloorHalfSize.value.set(this.roomSize.width / 2, this.roomSize.depth / 2);
    if (env?.backgroundColor) this._floorFade.uFadeColor.value.set(env.backgroundColor);
  }

  /** The trace can't run the fade shader, so the floor dissolves into scene.background through an alphaMap instead. */
  applyTraceFloorFade() {
    this.revertTraceFloorFade();
    const material = this.floor?.material;
    if (!material || !this._wantsFloorFade()) return;
    this._traceFloorFade = { material, alphaMap: material.alphaMap, transparent: material.transparent };
    this._traceFloorFadeMap ??= createFloorFadeAlphaMap();
    material.alphaMap = this._traceFloorFadeMap;
    material.transparent = true;
    material.needsUpdate = true;
  }

  revertTraceFloorFade() {
    const saved = this._traceFloorFade;
    if (!saved) return;
    this._traceFloorFade = null;
    saved.material.alphaMap = saved.alphaMap;
    saved.material.transparent = saved.transparent;
    saved.material.needsUpdate = true;
  }

  _dressFloorMaterial(material) {
    this._syncFloorFadeUniforms();
    if (!this._wantsFloorFade() || !material) return;
    applyFloorEdgeFade(material, this._floorFade);
  }

  setWallsVisible(visible) {
    this.walls.forEach(wall => wall.visible = visible);
  }

  setWindowsVisible(visible) {
    this.windows.forEach(win => win.visible = visible);
  }

  setCeilingVisible(visible) {
    if (this.ceiling) this.ceiling.visible = visible;
  }

  /**
   * Set ceiling material side for 2D/3D view modes
   * @param {boolean} is2DMode - true for 2D mode, false for 3D mode
   */
  setCeilingSide(is2DMode) {
    // Store the mode so it persists when ceiling is recreated
    this.is2DMode = is2DMode;

    if (this.ceiling && this.ceiling.material) {
      this.ceiling.material.side = is2DMode ? 0 : 2; // THREE.FrontSide = 0, THREE.DoubleSide = 2
      this.ceiling.material.needsUpdate = true;
    }
  }

  setTrimsVisible(visible) {
    this.trims.forEach(trim => trim.visible = visible);
  }

  setBaseboardsVisible(visible) {
    this.trims.forEach(trim => {
      if (trim.userData.isBaseboard) trim.visible = visible;
    });
  }

  setCrownMoldingsVisible(visible) {
    this.trims.forEach(trim => {
      if (trim.userData.isCrownMolding) trim.visible = visible;
    });
  }

  // ===== ROOM SIZE =====

  changeRoomSize(width, depth, height = null) {
    const MIN_SIZE = 1, MAX_SIZE = 50;
    const MIN_HEIGHT = 2.5, MAX_HEIGHT = 15;

    if (width < MIN_SIZE || width > MAX_SIZE || depth < MIN_SIZE || depth > MAX_SIZE) {
      console.warn(`Room dimensions must be between ${MIN_SIZE} and ${MAX_SIZE}`);
      return false;
    }

    if (height !== null && (height < MIN_HEIGHT || height > MAX_HEIGHT)) {
      console.warn(`Room height must be between ${MIN_HEIGHT} and ${MAX_HEIGHT}`);
      return false;
    }

    this.roomSize.width = width;
    this.roomSize.depth = depth;
    if (height !== null) this.roomSize.height = height;

    this.createRoom();
    return true;
  }

  getRoomSize() {
    return { ...this.roomSize };
  }

  setRoomSize(roomSize) {
    if (roomSize.width) this.roomSize.width = roomSize.width;
    if (roomSize.depth) this.roomSize.depth = roomSize.depth;
    if (roomSize.height) this.roomSize.height = roomSize.height;
  }

  // ===== UTILITIES =====

  // envMap is deliberately absent: it is the scene-wide environment texture, not the material's.
  _disposeMaterial(material) {
    if (!material) return;
    for (const slot of MATERIAL_TEXTURE_SLOTS) material[slot]?.dispose();
    material.dispose();
  }

  _removeAndDispose(scene, root) {
    if (root.parent) root.removeFromParent();
    else scene.remove(root);
    root.traverse((obj) => {
      obj.geometry?.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => this._disposeMaterial(m));
    });
  }

  dispose() {
    this.removeExistingRoom();
  }
}
